import { useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';

/**
 * Frontend mirror of backend/permissions.py.
 * Levels: no < view < user < manager < admin. `company.permissions` holds the effective level per module
 * (owners/admins always 'admin'). Use `usePerm(moduleKey)` in a page, or tag elements with
 * data-perm-write / data-perm-manage / data-perm-admin and call `usePermScope(moduleKey)` to auto-hide them via CSS.
 */
export const LEVELS = ['no', 'view', 'user', 'manager', 'admin'];
export const RANK = Object.fromEntries(LEVELS.map((l, i) => [l, i]));
export const LEVEL_LABEL = {
  no: { id: 'Tidak ada', en: 'No access' }, view: { id: 'Lihat', en: 'View' }, user: { id: 'User (data sendiri)', en: 'User (own data)' },
  manager: { id: 'Manager', en: 'Manager' }, admin: { id: 'Administrator', en: 'Administrator' },
};
export const PERM_MODULES = ['dashboard', 'website', 'settings', 'contacts', 'discuss', 'documents', 'crm', 'sales', 'purchasing', 'inventory', 'assets', 'accounting', 'hr', 'pillot_pay', 'ecommerce', 'pos', 'webmail', 'project'];

export const levelOf = (company, moduleKey) => {
  if (!company) return 'no';
  if (['owner', 'admin'].includes(company.role)) return 'admin';
  return company.permissions?.[moduleKey] || (moduleKey === 'webmail' ? 'user' : 'manager');
};
export const atLeast = (level, min) => (RANK[level] ?? 0) >= RANK[min];

export const usePerm = (moduleKey) => {
  const { company } = useAuth();
  const level = levelOf(company, moduleKey);
  return { level, canView: atLeast(level, 'view'), canWrite: atLeast(level, 'user'), canManage: atLeast(level, 'manager'), isAdmin: level === 'admin', ownOnly: level === 'user' };
};

/** Sets <body data-perm-level> so CSS can hide write/manage/admin controls (also inside portaled dialogs). */
export const usePermScope = (moduleKey) => {
  const { level } = usePerm(moduleKey);
  useEffect(() => {
    document.body.dataset.permLevel = level;
    document.body.dataset.permModule = moduleKey;
    return () => { delete document.body.dataset.permLevel; delete document.body.dataset.permModule; };
  }, [level, moduleKey]);
  return level;
};
